/**
 * Helper sticker chips (THE-GRID-NOTES.md §2).
 *
 * Helpers never get swim lanes. A helper renders as a draggable sticker chip:
 *   * pinned to their lead's lane header when paired today, or
 *   * sitting on the Helper bench strip when unattached (solo / unbound).
 *
 * Dragging a chip onto a lane header opens a scope popover (just today /
 * rest of this week / make default); dragging to the bench writes an
 * explicit solo-today override. Every pairing change gets a 10-second undo
 * toast that executes the exact inverse of what was applied.
 *
 * The chips speak the existing pairing endpoints:
 *   PUT    /api/dispatch/pairings   { date, helperId, leadId | null }
 *   DELETE /api/dispatch/pairings?date=&helperId=
 * and "make default" additionally PATCHes /api/users/:id
 * { defaultLeadUserId } then clears now-redundant future overrides.
 */
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarOff, Undo2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { addDaysToDateKey } from "@/lib/datetime";

/** Custom DnD MIME type so helper-chip drags never collide with job drags
 *  (which use "text/plain" job ids across the whole dispatch surface). */
export const HELPER_DRAG_MIME = "application/x-helper-id";

/** HELPER-MODE V3: dragging a TECH's name onto another tech's lane demotes
 *  him to acting-helper for the chosen scope. Separate MIME so lane-header
 *  drops can tell a tech demotion from a helper re-pair. */
export const TECH_DRAG_MIME = "application/x-tech-id";

const UNDO_TOAST_MS = 10_000;

export interface HelperChipInfo {
    id: string;
    name: string;
    /** Effective lead today (null when solo / unbound). */
    leadId: string | null;
    leadName: string | null;
    /** Where today's pairing comes from. */
    source: "default" | "override" | "unbound";
    soloToday: boolean;
    /** The helper's everyday lead, regardless of today's override. */
    defaultLeadId: string | null;
    defaultLeadName: string | null;
    /** Jobs assigned directly to the helper today — drives the ⚠ state. */
    directJobsCount: number;
    /** #1268 — set when this helper is blocked out on the shown date ("BPAT
     *  class", "PTO"). A helper has no lane of their own, so without this the
     *  chip sat on the lead's header looking available all week. */
    outLabel?: string | null;
}

/** Server row shape from GET /api/dispatch/pairings. */
export interface PairingRow {
    helperId: string;
    helperName: string;
    leadId: string | null;
    leadName: string | null;
    defaultLeadId: string | null;
    defaultLeadName: string | null;
    source: "default" | "override" | "unbound";
    soloToday: boolean;
}

/**
 * True for a FULL tech (not flagged as a helper, no everyday lead) who is a
 * helper today only because of a pairing row. For him "work solo" must DELETE
 * the row: any row — even a solo one — keeps him a helper with no lane
 * (dispatchPairings.ts eligible_helpers). Real helpers keep the solo row.
 */
export function isFullTechPairedToday(
    helper: Pick<HelperChipInfo, "id" | "source" | "defaultLeadId">,
    techs: ReadonlyArray<{ id: string; is_field_helper?: boolean | null }>,
): boolean {
    if (helper.source !== "override" || helper.defaultLeadId) return false;
    const row = techs.find((t) => t.id === helper.id);
    return !!row && !row.is_field_helper;
}

/**
 * The selected day plus every following weekday of the same workweek
 * (stops before Saturday). Weekend start days return just themselves —
 * "rest of this week" has no meaning on a Saturday.
 */
export function remainingWorkdays(dateKey: string): string[] {
    const dow = (key: string) => new Date(`${key}T12:00:00`).getDay();
    const start = dow(dateKey);
    if (start === 0 || start === 6) return [dateKey];
    const dates = [dateKey];
    let d = dateKey;
    while (dates.length < 7) {
        d = addDaysToDateKey(d, 1);
        const w = dow(d);
        if (w === 0 || w === 6) break;
        dates.push(d);
    }
    return dates;
}

/* ─── Pairing actions with exact-inverse undo ───────────────────────── */

/** One reversible override mutation on a single (date, helper). */
interface OverrideOp {
    date: string;
    helperId: string;
    /** State before: an override row existed (with this leadId) or not. */
    prev: { had: boolean; leadId: string | null };
    /** State after: write an override (leadId | null=solo) or delete it. */
    next: { set: true; leadId: string | null } | { set: false };
}

async function fetchPairingRow(date: string, helperId: string): Promise<PairingRow | null> {
    const res = await api.get<{ pairings: PairingRow[] }>(`/api/dispatch/pairings?date=${encodeURIComponent(date)}`);
    return res.pairings.find((p) => p.helperId === helperId) ?? null;
}

function prevFromRow(row: PairingRow | null): { had: boolean; leadId: string | null } {
    if (!row || row.source !== "override") return { had: false, leadId: null };
    return { had: true, leadId: row.soloToday ? null : row.leadId };
}

async function runOverrideOp(op: OverrideOp, direction: "forward" | "inverse"): Promise<void> {
    const target =
        direction === "forward"
            ? op.next
            : op.prev.had
              ? { set: true as const, leadId: op.prev.leadId }
              : { set: false as const };
    if (target.set) {
        await api.put("/api/dispatch/pairings", {
            date: op.date,
            helperId: op.helperId,
            leadId: target.leadId,
        });
    } else {
        await api.delete(
            `/api/dispatch/pairings?date=${encodeURIComponent(op.date)}&helperId=${encodeURIComponent(op.helperId)}`,
        );
    }
}

export interface PairingActions {
    /** Pair helper with a lead (or null = solo) for the given dates. */
    setOverride: (helper: HelperChipInfo, leadId: string | null, leadName: string | null, dates: string[]) => void;
    /** Drop today's override, reverting the helper to their default. */
    revertToDefault: (helper: HelperChipInfo, date: string) => void;
    /** Make the lead the helper's everyday default + clear redundant future overrides. */
    makeDefault: (helper: HelperChipInfo, leadId: string, leadName: string, fromDate: string) => void;
}

export function usePairingActions(): PairingActions {
    const qc = useQueryClient();

    const refresh = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["dispatch", "pairings"] });
    }, [qc]);

    const undoToast = useCallback(
        (label: string, undo: () => Promise<void>) => {
            toast.success(label, {
                duration: UNDO_TOAST_MS,
                action: {
                    label: "Undo",
                    onClick: () => {
                        undo()
                            .then(() => {
                                refresh();
                                toast.success("Pairing change undone.");
                            })
                            .catch(() => toast.error("Undo failed — check the pairings and retry."));
                    },
                },
            });
        },
        [refresh],
    );

    const applyOps = useCallback(
        async (ops: OverrideOp[], label: string) => {
            for (const op of ops) await runOverrideOp(op, "forward");
            refresh();
            undoToast(label, async () => {
                for (const op of [...ops].reverse()) await runOverrideOp(op, "inverse");
            });
        },
        [refresh, undoToast],
    );

    const setOverride = useCallback(
        (helper: HelperChipInfo, leadId: string | null, leadName: string | null, dates: string[]) => {
            (async () => {
                // Capture each day's prior state first so undo restores it exactly
                // (other days can hold their own overrides we know nothing about).
                const ops: OverrideOp[] = [];
                for (const date of dates) {
                    const row = await fetchPairingRow(date, helper.id);
                    ops.push({
                        date,
                        helperId: helper.id,
                        prev: prevFromRow(row),
                        next: { set: true, leadId },
                    });
                }
                const scope =
                    dates.length === 1 ? "today" : `through ${dates[dates.length - 1]} (${dates.length} days)`;
                const label =
                    leadId === null
                        ? `${helper.name} — solo ${scope}`
                        : `${helper.name} rides with ${leadName ?? "lead"} ${scope}`;
                await applyOps(ops, label);
            })().catch(() => toast.error("Pairing change failed — please try again."));
        },
        [applyOps],
    );

    const revertToDefault = useCallback(
        (helper: HelperChipInfo, date: string) => {
            if (helper.source !== "override") return;
            const ops: OverrideOp[] = [
                {
                    date,
                    helperId: helper.id,
                    prev: { had: true, leadId: helper.soloToday ? null : helper.leadId },
                    next: { set: false },
                },
            ];
            const label = helper.defaultLeadName
                ? `${helper.name} is back with ${helper.defaultLeadName}`
                : `${helper.name} is back on their own today`;
            applyOps(ops, label).catch(() => toast.error("Pairing change failed — please try again."));
        },
        [applyOps],
    );

    const makeDefault = useCallback(
        (helper: HelperChipInfo, leadId: string, leadName: string, fromDate: string) => {
            (async () => {
                const prevDefault = helper.defaultLeadId;
                // Overrides that now merely repeat the new default are redundant —
                // clear them for the remaining workdays so "no row = default" holds.
                const clearedOps: OverrideOp[] = [];
                for (const date of remainingWorkdays(fromDate)) {
                    const row = await fetchPairingRow(date, helper.id);
                    if (row && row.source === "override" && !row.soloToday && row.leadId === leadId) {
                        clearedOps.push({
                            date,
                            helperId: helper.id,
                            prev: { had: true, leadId },
                            next: { set: false },
                        });
                    }
                }
                await api.patch(`/api/users/${helper.id}`, { defaultLeadUserId: leadId });
                for (const op of clearedOps) await runOverrideOp(op, "forward");
                qc.invalidateQueries({ queryKey: ["dispatch", "pairings"] });
                qc.invalidateQueries({ queryKey: ["user-profile", helper.id] });
                undoToast(`${helper.name} now rides with ${leadName} by default`, async () => {
                    await api.patch(`/api/users/${helper.id}`, { defaultLeadUserId: prevDefault });
                    for (const op of [...clearedOps].reverse()) await runOverrideOp(op, "inverse");
                    qc.invalidateQueries({ queryKey: ["user-profile", helper.id] });
                });
            })().catch(() => toast.error("Could not set the default lead — please try again."));
        },
        [qc, undoToast],
    );

    return { setOverride, revertToDefault, makeDefault };
}

/* ─── Chip ──────────────────────────────────────────────────────────── */

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join("");
}

export interface HelperChipProps {
    helper: HelperChipInfo;
    /** Rendered on the bench (vs pinned to a lane header). */
    onBench?: boolean;
    onDragStart: (helperId: string) => void;
    onDragEnd: () => void;
    /** One-click revert-to-default (only rendered for overrides). */
    onRevert?: (helper: HelperChipInfo) => void;
    /** Out chips only — opens move / clear for that day. */
    onOutClick?: (helper: HelperChipInfo) => void;
}

/**
 * The sticker itself. Solid = default pairing; dashed accent = manual
 * override today (with a one-click ↺ back-to-default); amber ⚠ when the
 * helper holds direct job assignments that should be moved.
 */
export function HelperChip({ helper, onBench = false, onDragStart, onDragEnd, onRevert, onOutClick }: HelperChipProps) {
    const isOverride = helper.source === "override";
    const warn = helper.directJobsCount > 0;
    // #1268 — out for the day. The chip stays on its lead's lane rather than
    // moving to the bench: the bench means "working alone today", which is a
    // different (and available) thing. It just stops claiming they're here.
    const out = (helper.outLabel ?? "").trim();
    const isOut = out.length > 0;
    // The out chip used to be inert, and the only way to change the day was
    // the bench's "Mark someone out" dialog. Clicking it now opens move/clear.
    const outClickable = isOut && Boolean(onOutClick);

    const tooltip = (() => {
        if (isOut) {
            const where = helper.leadName ? `Would ride with ${helper.leadName}` : "No lead today";
            const tail = outClickable ? " · Click to move or clear" : "";
            return `${helper.name} is out — ${out} · ${where} · Not on the schedule for this day${tail}`;
        }
        const bits: string[] = [];
        if (helper.soloToday) bits.push("Solo today (override)");
        else if (helper.leadName)
            bits.push(
                isOverride ? `With ${helper.leadName} today (override)` : `Rides with ${helper.leadName} (default)`,
            );
        else bits.push("Unpaired — no lead today");
        if (isOverride && helper.defaultLeadName) bits.push(`Default: ${helper.defaultLeadName}`);
        if (warn)
            bits.push(
                `Has ${helper.directJobsCount} job${helper.directJobsCount === 1 ? "" : "s"} assigned directly — move them to the lead`,
            );
        bits.push("Drag onto a technician lane to pair");
        return bits.join(" · ");
    })();

    return (
        <span
            draggable={!isOut}
            data-testid={isOut ? "helper-chip-out" : "helper-chip"}
            onDragStart={(e) => {
                if (isOut) return;
                e.dataTransfer.setData(HELPER_DRAG_MIME, helper.id);
                e.dataTransfer.effectAllowed = "move";
                onDragStart(helper.id);
            }}
            onDragEnd={onDragEnd}
            role={outClickable ? "button" : undefined}
            tabIndex={outClickable ? 0 : undefined}
            onClick={outClickable ? () => onOutClick?.(helper) : undefined}
            onKeyDown={
                outClickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onOutClick?.(helper);
                          }
                      }
                    : undefined
            }
            title={tooltip}
            className={cn(
                "inline-flex max-w-full select-none items-center gap-1 rounded-full border py-0.5 pl-0.5 pr-1.5 text-[10px] font-medium leading-none",
                isOut
                    ? "border-dashed border-rose-400 bg-rose-50 text-rose-700 dark:border-rose-500 dark:bg-rose-500/15 dark:text-rose-200"
                    : "",
                isOut && !outClickable && "cursor-default",
                outClickable &&
                    "cursor-pointer hover:border-slate-500 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary dark:hover:bg-slate-700",
                !isOut && "cursor-grab active:cursor-grabbing",
                !isOut && isOverride
                    ? "border-dashed border-violet-400 bg-violet-50 text-violet-800 dark:border-violet-500 dark:bg-violet-900/40 dark:text-violet-100"
                    : !isOut && helper.source === "default"
                      ? "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-600 dark:bg-sky-900/50 dark:text-sky-100"
                      : !isOut
                        ? "border-border bg-muted text-foreground/80"
                        : "",
                onBench && "py-1 text-[11px]",
            )}
        >
            <span
                aria-hidden
                className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold",
                    isOut
                        ? "bg-rose-200 text-rose-700 dark:bg-rose-500/40 dark:text-rose-50"
                        : isOverride
                          ? "bg-violet-200 text-violet-800 dark:bg-violet-700 dark:text-violet-50"
                          : "bg-sky-200 text-sky-800 dark:bg-sky-700 dark:text-sky-50",
                    onBench && "h-5 w-5 text-[9px]",
                )}
            >
                {initials(helper.name) || "?"}
            </span>
            <span className={cn("truncate", isOut && "line-through decoration-rose-400")}>{helper.name}</span>
            {isOut && <span className="shrink-0 whitespace-nowrap font-semibold uppercase">· Out — {out}</span>}
            {!isOut && warn && (
                <AlertTriangle
                    className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400"
                    aria-label={`${helper.directJobsCount} direct job${helper.directJobsCount === 1 ? "" : "s"} — move them`}
                />
            )}
            {!isOut && isOverride && onRevert && (
                <button
                    type="button"
                    className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-violet-200 dark:hover:bg-violet-700"
                    title={
                        helper.defaultLeadName
                            ? `Back to default (${helper.defaultLeadName})`
                            : "Clear today's override"
                    }
                    onClick={(e) => {
                        e.stopPropagation();
                        onRevert(helper);
                    }}
                >
                    <Undo2 className="h-3 w-3" />
                </button>
            )}
        </span>
    );
}

/* ─── Helper bench ──────────────────────────────────────────────────── */

export interface HelperBenchProps {
    /** Unattached helpers (solo today or unbound) — visibly "not deployed". */
    helpers: HelperChipInfo[];
    /** True when any helper-eligible users exist at all (controls render). */
    hasHelpers: boolean;
    draggingHelperId: string | null;
    onDragStart: (helperId: string) => void;
    onDragEnd: () => void;
    onRevert: (helper: HelperChipInfo) => void;
    /** Chip dropped on the bench = explicit solo-today override. */
    onBenchDrop: (helperId: string) => void;
    /** #1268 — helpers have no lane to click, so the bench carries the way in
     *  to "mark someone out". */
    onMarkOut?: () => void;
    /** Clicking an out helper's chip — move or clear that day. */
    onOutClick?: (helper: HelperChipInfo) => void;
}

export function HelperBench({
    helpers,
    hasHelpers,
    draggingHelperId,
    onDragStart,
    onDragEnd,
    onRevert,
    onBenchDrop,
    onMarkOut,
    onOutClick,
}: HelperBenchProps) {
    if (!hasHelpers) return null;
    const dragActive = draggingHelperId != null;
    return (
        <div
            className={cn(
                "flex flex-wrap items-center gap-2 rounded-lg border border-dashed bg-card px-3 py-2 transition-colors",
                dragActive && "border-sky-400 bg-sky-50/80 ring-1 ring-inset ring-sky-300 dark:bg-sky-950/40",
            )}
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(HELPER_DRAG_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => {
                const helperId = e.dataTransfer.getData(HELPER_DRAG_MIME);
                if (!helperId) return;
                e.preventDefault();
                onBenchDrop(helperId);
            }}
        >
            <span
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground/70"
                title="Helpers don't get their own row on the board — they ride along with a lead technician. A name chip here means that helper isn't paired with anyone today."
            >
                <Users className="h-3.5 w-3.5" />
                Helper bench
            </span>
            {helpers.length === 0 ? (
                <span className="text-[11px] italic text-muted-foreground">
                    {dragActive
                        ? "Drop here to have them work on their own today"
                        : "Every helper is riding with a lead tech today"}
                </span>
            ) : (
                helpers.map((h) => (
                    <HelperChip
                        key={h.id}
                        helper={h}
                        onBench
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onRevert={onRevert}
                        onOutClick={onOutClick}
                    />
                ))
            )}
            <span className="ml-auto text-[10px] text-muted-foreground">
                Drag a helper's name onto a tech's row to pair them for the day · drop it back here to have them work on
                their own
            </span>
            {onMarkOut && (
                <button
                    type="button"
                    data-testid="bench-mark-out"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
                    title="Mark anyone out for a day or a range — a class, a school week, jury duty"
                    onClick={onMarkOut}
                >
                    <CalendarOff className="h-3 w-3" />
                    Mark someone out
                </button>
            )}
        </div>
    );
}

/* ─── Drop-scope popover ────────────────────────────────────────────── */

export interface ChipDropTarget {
    helper: HelperChipInfo;
    leadId: string;
    leadName: string;
    x: number;
    y: number;
}

export interface ChipDropPopoverProps {
    target: ChipDropTarget;
    onScope: (scope: "today" | "week" | "default") => void;
    onCancel: () => void;
    /** V3 tech demotion: a full tech never gets a permanent default lead
     *  from a drag — hide the "make default" option. */
    hideDefault?: boolean;
}

/**
 * Scope picker shown after a chip lands on a lane header: apply the pairing
 * just for today, for the rest of this workweek, or as the new default.
 */
export function ChipDropPopover({ target, onScope, onCancel, hideDefault = false }: ChipDropPopoverProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        const onPointerDown = (e: PointerEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
        };
        window.addEventListener("keydown", onKey);
        window.addEventListener("pointerdown", onPointerDown);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [onCancel]);

    const left = Math.min(target.x, typeof window !== "undefined" ? window.innerWidth - 260 : target.x);
    const top = Math.min(target.y, typeof window !== "undefined" ? window.innerHeight - 180 : target.y);

    const item =
        "flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-secondary hover:text-secondary-foreground";

    return (
        <div
            ref={ref}
            role="menu"
            className="fixed z-50 w-60 rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg"
            style={{ left, top }}
        >
            <div className="px-2 py-1.5 text-xs font-semibold text-foreground/70">
                {target.helper.name} → {target.leadName}
            </div>
            <button type="button" role="menuitem" className={item} onClick={() => onScope("today")}>
                Just today
            </button>
            <button type="button" role="menuitem" className={item} onClick={() => onScope("week")}>
                Rest of this week
            </button>
            {!hideDefault && (
                <button type="button" role="menuitem" className={item} onClick={() => onScope("default")}>
                    Make this the new default
                </button>
            )}
            <div className="-mx-1 my-1 h-px bg-border" />
            <button type="button" role="menuitem" className={cn(item, "text-foreground/60")} onClick={onCancel}>
                Cancel
            </button>
        </div>
    );
}
